import React from 'react';
import { Text, StyleSheet, Pressable, ActivityIndicator } from 'react-native';
import { webColors, webBorderRadius } from '../../constants/webStyles';

const WebButton = ({
  children,
  onPress,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  style,
}) => {
  const getVariantStyles = (hovered, pressed) => {
    const base = {
      primary: {
        backgroundColor: webColors.primary,
        borderWidth: 0,
      },
      secondary: {
        backgroundColor: webColors.secondary,
        borderWidth: 0,
      },
      outline: {
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderColor: webColors.primary,
      },
      ghost: {
        backgroundColor: 'transparent',
        borderWidth: 0,
      },
      danger: {
        backgroundColor: '#dc2626',
        borderWidth: 0,
      },
      success: {
        backgroundColor: webColors.success,
        borderWidth: 0,
      },
    };

    const hover = {
      primary: { backgroundColor: webColors.primaryDark },
      secondary: { backgroundColor: '#1e429f' },
      outline: { backgroundColor: `${webColors.primary}10` },
      ghost: { backgroundColor: webColors.background },
      danger: { backgroundColor: '#b91c1c' },
      success: { backgroundColor: '#16a34a' },
    };

    let variantStyle = base[variant] || base.primary;
    if (hovered && !disabled) {
      variantStyle = { ...variantStyle, ...hover[variant] };
    }
    if (pressed && !disabled) {
      variantStyle = { ...variantStyle, opacity: 0.9 };
    }
    return variantStyle;
  };

  const getTextColor = () => {
    if (variant === 'outline' || variant === 'ghost') {
      return webColors.primary;
    }
    return '#ffffff';
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          button: { paddingHorizontal: 12, paddingVertical: 6 },
          text: { fontSize: 13 },
        };
      case 'large':
        return {
          button: { paddingHorizontal: 28, paddingVertical: 14 },
          text: { fontSize: 16 },
        };
      default:
        return {
          button: { paddingHorizontal: 20, paddingVertical: 10 },
          text: { fontSize: 14 },
        };
    }
  };

  const sizeStyles = getSizeStyles();

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ hovered, pressed }) => [
        styles.button,
        sizeStyles.button,
        getVariantStyles(hovered, pressed),
        fullWidth && styles.fullWidth,
        disabled && styles.disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={getTextColor()}
        />
      ) : (
        <>
          {leftIcon}
          <Text
            style={[
              styles.text,
              sizeStyles.text,
              { color: getTextColor() },
              disabled && styles.textDisabled,
            ]}
          >
            {children}
          </Text>
          {rightIcon}
        </>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: webBorderRadius.medium,
    transitionDuration: '200ms',
    transitionProperty: 'background-color, opacity',
    cursor: 'pointer',
  },
  fullWidth: {
    width: '100%',
  },
  disabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  text: {
    fontWeight: '600',
    textAlign: 'center',
  },
  textDisabled: {
    opacity: 0.7,
  },
});

export default WebButton;
